import { AddressSearchInput } from '@/components/global/address/AddressSearchInput';
import { GoogleMapsProvider } from '@/components/global/address/GoogleMapsProviderSingleton';
import InputError from '@/components/layout/InputError';
import { CompanyRegistryCombobox, type CompanyDetailsResponse, type CompanyRegistrySelection } from '@/components/ui/company-registry-combobox';
import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/shadcn/card';
import { Combobox } from '@/components/ui/shadcn/combobox';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { MultiSelect } from '@/components/ui/shadcn/multi-select';
import { useSettings } from '@/contexts/SettingsContext';
import { useCompanyCategoryOptions, useCompanyTypeOptions } from '@/hooks/useTranslatedSettingOptions';
import { useTranslations } from '@/hooks/useTranslations';
import { csrfFetch } from '@/lib/csrfFetch';
import { cn } from '@/lib/utils';
import { createCompanyValidationSchema } from '@/lib/validations/company-validation';
import type { CompanyFormData } from '@/types/forms/companies';
import { useForm } from '@inertiajs/react';
import { Building2, Info, Mail, MapPin, Phone } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { toast } from 'sonner';

const invalidFieldClassName = 'border-destructive ring-destructive/20 ring-[3px]';

/** What the store endpoint hands back to an AJAX caller. */
export interface CreatedCompany {
    id: string;
    name: string;
    company_code: string;
    country: string;
}

interface AddCompanyFormProps {
    /**
     * Set when the form is embedded — a sheet opened from a company picker.
     * The created company is handed back instead of navigating to its page, so
     * the picker can select it and the caller stays where it was.
     */
    onCompanyCreated?: (company: CreatedCompany) => void;
    /** Replaces the "go back" cancel, which means nothing inside a sheet. */
    onCancel?: () => void;
    /**
     * Registry code to pull in on mount, for the case where the user picked a
     * "not in CRM" registry hit somewhere else: the form opens already filled with
     * what the registry knows, so only the missing fields are left to complete.
     */
    prefillRegistryCode?: string | null;
}

export default function AddCompanyForm({ onCompanyCreated, onCancel, prefillRegistryCode = null }: AddCompanyFormProps = {}) {
    const { legacy, settings } = useSettings();
    const { t } = useTranslations();
    const companyTypeOptions = useCompanyTypeOptions();
    const companyCategoryOptions = useCompanyCategoryOptions();

    const validationSchema = useMemo(() => {
        if (!(settings.countries?.length > 0)) {
            return null;
        }
        return createCompanyValidationSchema({
            countries: settings.countries.map((country: { value: string }) => country.value),
            companyCategories: settings.companyCategories,
            companyTypes: settings.companyTypes,
            companyPriorities: settings.companyPriorities,
            messages: {
                name_required: t('companies.forms.add.validation.name_required', 'Company name is required.'),
                name_max: t('companies.forms.add.validation.name_max', 'Company name cannot exceed 255 characters.'),
                company_code_required: t('companies.forms.add.validation.company_code_required', 'Company code is required.'),
                company_type_required: t('companies.forms.add.validation.company_type_required', 'Company type is required.'),
                country_required: t('companies.forms.add.validation.country_required', 'Country is required.'),
                category_invalid: t('companies.forms.add.validation.category_invalid', 'Selected category is invalid.'),
                phone_invalid: t('companies.forms.add.validation.phone_invalid', 'Please enter a valid phone number.'),
                email_invalid: t('companies.forms.add.validation.email_invalid', 'Please enter a valid email address.'),
            },
        });
    }, [settings.countries, settings.companyCategories, settings.companyTypes, settings.companyPriorities, t]);

    const { data, setData, post, errors, setError, clearErrors, processing } = useForm<CompanyFormData>({
        name: '',
        category: [],
        domain: '',
        address: '',
        address_lat: null,
        address_lng: null,
        address_street: null,
        address_house_number: null,
        address_postal_code: null,
        address_city: null,
        address_municipality: null,
        address_country: null,
        country: '',
        company_code: '',
        vat: '',
        phone: '',
        email: '',
        type: '',
    });

    const [companyRegistryLabel, setCompanyRegistryLabel] = useState('');
    const [hasAutoFilledFields, setHasAutoFilledFields] = useState(false);
    /** Embedded mode does its own request, so it tracks its own pending state. */
    const [submitting, setSubmitting] = useState(false);
    /** True while a prefillRegistryCode is being resolved into form values. */
    const [isPrefilling, setIsPrefilling] = useState(false);

    const requiredMark = <span className="text-destructive">*</span>;

    const countryLabel = useMemo(() => {
        const value = typeof data.country === 'string' ? data.country : '';
        if (!value) {
            return t('companies.forms.add.country_placeholder', '—');
        }
        const match = Array.isArray(legacy.COUNTRY_OPTIONS)
            ? legacy.COUNTRY_OPTIONS.find((option) => String(option.value).toUpperCase() === value.toUpperCase())
            : undefined;
        return match ? String(match.label) : value;
    }, [data.country, legacy.COUNTRY_OPTIONS, t]);

    const resolveCountryFromAddress = (countryCode?: string, countryName?: string): string | null => {
        const normalizedCode = countryCode?.trim().toUpperCase();
        const normalizedName = countryName?.trim().toLowerCase();

        if (Array.isArray(legacy.COUNTRY_OPTIONS)) {
            if (normalizedCode) {
                const byCode = legacy.COUNTRY_OPTIONS.find((option) => String(option.value).toUpperCase() === normalizedCode);
                if (byCode) return String(byCode.value);
            }

            if (normalizedName) {
                const byName = legacy.COUNTRY_OPTIONS.find((option) => String(option.label).toLowerCase() === normalizedName);
                if (byName) return String(byName.value);
            }
        }

        if (normalizedCode) return normalizedCode;
        return null;
    };

    const getCountryValue = (): string => {
        if (!data.country) return '';
        if (typeof data.country === 'string') return data.country;
        if (typeof data.country === 'object' && data.country !== null && 'value' in data.country) {
            return String((data.country as { value: unknown }).value);
        }
        return '';
    };

    const getTypeValue = (): string => {
        if (!data.type) return '';
        if (typeof data.type === 'string') return data.type;
        if (typeof data.type === 'object' && data.type !== null && 'value' in data.type) {
            return String((data.type as { value: unknown }).value);
        }
        return '';
    };

    const resetAddressFields = () => {
        setData('address', '');
        setData('address_street', null);
        setData('address_house_number', null);
        setData('address_postal_code', null);
        setData('address_city', null);
        setData('address_municipality', null);
        setData('address_country', null);
        setData('address_lat', null);
        setData('address_lng', null);
    };

    const clearAutoFilledFields = () => {
        setData('company_code', '');
        setData('vat', '');
        resetAddressFields();
        setHasAutoFilledFields(false);
    };

    const handleCompanyRegistrySelection = (selection: CompanyRegistrySelection | null) => {
        clearErrors('name');
        if (!selection) {
            setCompanyRegistryLabel('');
            setData('name', '');
            setHasAutoFilledFields(false);
            if (hasAutoFilledFields) clearAutoFilledFields();
            return;
        }

        if (selection.mode === 'manual') {
            setCompanyRegistryLabel(selection.name);
            setData('name', selection.name);
            setHasAutoFilledFields(false);
            if (hasAutoFilledFields) clearAutoFilledFields();
            return;
        }

        applyRegistryDetails(selection.details);
    };

    /** Fills name, code, VAT and address from a registry record — the shared path for a
        combobox pick and for a code handed in via prefillRegistryCode. */
    const applyRegistryDetails = (details: CompanyDetailsResponse) => {
        const displayLabel = [details.name, details.code].filter(Boolean).join(' — ');
        setCompanyRegistryLabel(displayLabel);
        setData('name', details.name ?? '');
        setData('company_code', details.code ?? '');
        setData('vat', details.vat ?? '');
        clearErrors('company_code');

        const address = details.address ?? null;
        if (address) {
            setData('address', address.label ?? '');
            setData('address_street', address.street ?? null);
            setData('address_house_number', address.house_number ?? null);
            setData('address_postal_code', address.postal_code ?? null);
            setData('address_city', address.city ?? null);
            setData('address_municipality', address.municipality ?? null);

            if (typeof address.lat === 'number' && typeof address.lon === 'number') {
                setData('address_lat', address.lat);
                setData('address_lng', address.lon);
            } else {
                setData('address_lat', null);
                setData('address_lng', null);
            }

            if (!getCountryValue()) {
                setData('country', 'LT');
                clearErrors('country');
            }
        } else {
            resetAddressFields();
        }

        setHasAutoFilledFields(true);
    };

    // Pull the registry record once when a code is handed in, so the form opens
    // pre-filled instead of asking the user to search for what they already picked.
    const prefilledCode = useRef<string | null>(null);
    useEffect(() => {
        const code = prefillRegistryCode?.trim();

        if (!code || prefilledCode.current === code) {
            return;
        }

        prefilledCode.current = code;
        let cancelled = false;
        setIsPrefilling(true);

        void (async () => {
            try {
                const response = await fetch(`/api/company/${encodeURIComponent(code)}`, {
                    headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                });

                if (!response.ok) {
                    throw new Error(`Company details failed with ${response.status}`);
                }

                const details = (await response.json()) as CompanyDetailsResponse;

                if (!cancelled) {
                    applyRegistryDetails(details);
                }
            } catch (error) {
                console.error('Company registry prefill failed:', error);

                // The code is still the one the user picked — keep it and let them fill the rest.
                if (!cancelled) {
                    setData('company_code', code);
                    toast.error(t('companies.forms.add.messages.prefill_failed', 'Could not load registry data — please fill the form in manually.'));
                }
            } finally {
                if (!cancelled) {
                    setIsPrefilling(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prefillRegistryCode]);

    /**
     * Embedded mode posts the same payload to the same endpoint, but asks for
     * JSON so the response can be handed to the caller rather than followed.
     */
    const submitEmbedded = async (payload: Record<string, unknown>) => {
        setSubmitting(true);

        try {
            const response = await csrfFetch(route('companies.store'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify(payload),
            });

            const result = await response.json().catch(() => null);

            if (!response.ok) {
                const validationErrors = (result?.errors ?? {}) as Record<string, string | string[]>;
                Object.entries(validationErrors).forEach(([field, message]) => {
                    setError(field as never, Array.isArray(message) ? message[0] : message);
                });

                toast.error(
                    Object.keys(validationErrors).length > 0
                        ? t('companies.forms.add.validation.resolve_errors', 'Please fill in all required fields.')
                        : t('companies.forms.add.messages.create_failed', 'Failed to create company.'),
                );

                return;
            }

            toast.success(t('companies.forms.add.messages.created', 'Company created.'));
            onCompanyCreated?.({ ...result, id: String(result.id) });
        } catch (error) {
            console.error('Failed to create company:', error);
            toast.error(t('companies.forms.add.messages.create_failed', 'Failed to create company.'));
        } finally {
            setSubmitting(false);
        }
    };

    const submit = (e: FormEvent) => {
        e.preventDefault();
        clearErrors();

        if (validationSchema) {
            try {
                const result = validationSchema.safeParse(data);
                if (!result.success) {
                    const seen = new Set<string>();
                    result.error.issues.forEach((err) => {
                        const field = err.path?.[0] !== undefined ? String(err.path[0]) : '';
                        if (!field || seen.has(field)) return;
                        seen.add(field);
                        setError(field as never, err.message);
                    });

                    toast.error(t('companies.forms.add.validation.resolve_errors', 'Please fill in all required fields.'));
                    return;
                }
            } catch (error: unknown) {
                console.error('Validation error:', error);
            }
        }

        const getAddressField = (value: string | null | undefined): string | null => {
            if (value === null || value === undefined) return null;
            const str = String(value).trim();
            return str.length > 0 ? str : null;
        };

        setData('address_lat', data.address_lat !== null && data.address_lat !== undefined ? data.address_lat : null);
        setData('address_lng', data.address_lng !== null && data.address_lng !== undefined ? data.address_lng : null);
        setData('address_street', getAddressField(data.address_street));
        setData('address_house_number', getAddressField(data.address_house_number));
        setData('address_postal_code', getAddressField(data.address_postal_code));
        setData('address_city', getAddressField(data.address_city));
        setData('address_municipality', getAddressField(data.address_municipality));
        setData('address_country', getAddressField(data.address_country as string | null));

        if (onCompanyCreated) {
            void submitEmbedded({
                ...data,
                address_lat: data.address_lat ?? null,
                address_lng: data.address_lng ?? null,
                address_street: getAddressField(data.address_street),
                address_house_number: getAddressField(data.address_house_number),
                address_postal_code: getAddressField(data.address_postal_code),
                address_city: getAddressField(data.address_city),
                address_municipality: getAddressField(data.address_municipality),
                address_country: getAddressField(data.address_country as string | null),
            });

            return;
        }

        post(route('companies.store'), {
            onError: (errors: Record<string, string>) => {
                console.error('Backend errors:', errors);
            },
        });
    };

    return (
        <form onSubmit={submit} className="space-y-6 pb-6">
            <Card className="rounded-md">
                <CardHeader className="pb-4">
                    <div className="flex items-start gap-3">
                        <span className="bg-primary/12 text-primary inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold">
                            1
                        </span>
                        <div className="space-y-1">
                            <CardTitle className="flex items-center gap-2">
                                <Building2 className="h-4 w-4" />
                                {t('companies.forms.add.sections_long.identification', 'Company identification')}
                            </CardTitle>
                            <CardDescription>
                                {t('companies.forms.add.sections_long.identification_desc', 'Required fields are marked with *.')}
                            </CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="company_registry">
                            {t('companies.forms.add.labels.company', 'Company')} {requiredMark}
                        </Label>
                        <CompanyRegistryCombobox
                            value={companyRegistryLabel}
                            onSelectionChange={handleCompanyRegistrySelection}
                            placeholder={t('companies.forms.add.placeholders.enter_company_name', 'Enter company name or code')}
                            searchPlaceholder={t('companies.forms.add.placeholders.search_company', 'Enter company name or code')}
                            emptyText={t('companies.forms.add.placeholders.no_companies', 'No companies found')}
                            existingBadgeText={t('companies.forms.add.badges.existing', 'Existing')}
                            className={cn(errors.name && invalidFieldClassName)}
                        />
                        <InputError message={errors.name} />
                    </div>

                    <div className="grid items-start gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                            <Label htmlFor="type">
                                {t('companies.forms.add.labels.type', 'Type')} {requiredMark}
                            </Label>
                            <Combobox
                                options={companyTypeOptions}
                                value={getTypeValue()}
                                onValueChange={(value) => {
                                    clearErrors('type');
                                    setData('type', value);
                                }}
                                placeholder={t('companies.forms.add.placeholders.select_company_type', 'Select company type...')}
                                searchPlaceholder={t('companies.forms.add.placeholders.search_company_type', 'Search company type...')}
                                emptyText={t('companies.forms.add.placeholders.no_company_type', 'No company type found.')}
                                className={cn(errors.type && invalidFieldClassName)}
                            />
                            <InputError message={errors.type} />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="company_code">
                                {t('companies.forms.add.labels.code', 'Code')} {requiredMark}
                            </Label>
                            <Input
                                id="company_code"
                                value={data.company_code}
                                aria-invalid={!!errors.company_code}
                                className={cn(errors.company_code && invalidFieldClassName)}
                                onChange={(e) => {
                                    clearErrors('company_code');
                                    setHasAutoFilledFields(false);
                                    setData('company_code', e.target.value);
                                }}
                                autoComplete="off"
                                placeholder={t('companies.forms.add.placeholders.company_code', 'Company code')}
                            />
                            <InputError message={errors.company_code} />
                        </div>
                    </div>

                    <div className="grid items-start gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                            <Label htmlFor="vat">{t('companies.forms.add.labels.vat', 'VAT')}</Label>
                            <Input
                                id="vat"
                                value={data.vat}
                                aria-invalid={!!errors.vat}
                                className={cn(errors.vat && invalidFieldClassName)}
                                onChange={(e) => {
                                    clearErrors('vat');
                                    setHasAutoFilledFields(false);
                                    setData('vat', e.target.value);
                                }}
                                autoComplete="off"
                                placeholder={t('companies.forms.add.placeholders.vat_code', 'VAT code')}
                            />
                            <InputError message={errors.vat} />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="category">{t('companies.forms.add.labels.category', 'Category')}</Label>
                            <MultiSelect
                                options={companyCategoryOptions}
                                selected={data.category}
                                onChange={(selected: string[]) => {
                                    clearErrors('category');
                                    setData('category', selected);
                                }}
                                placeholder={t('companies.forms.add.placeholders.select_categories', 'Select categories...')}
                                searchPlaceholder={t('companies.forms.add.placeholders.search_categories', 'Search categories...')}
                                emptyText={t('companies.forms.add.placeholders.no_categories', 'No categories found.')}
                                className={cn(errors.category && invalidFieldClassName)}
                            />
                            <InputError message={errors.category} />
                        </div>
                    </div>
                </CardContent>
            </Card>

            <Card className="rounded-md">
                <CardHeader className="pb-4">
                    <div className="flex items-start gap-3">
                        <span className="bg-primary/12 text-primary inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold">
                            2
                        </span>
                        <div className="space-y-1">
                            <CardTitle className="flex items-center gap-2">
                                <MapPin className="h-4 w-4" />
                                {t('companies.forms.add.sections_long.address', 'Address')}
                            </CardTitle>
                            <CardDescription>
                                {t('companies.forms.add.sections_long.address_desc', 'Company location or registered address.')}
                            </CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="address">{t('companies.forms.add.labels.address', 'Address')}</Label>
                        <GoogleMapsProvider>
                            <div className={cn(errors.address && '[&_input]:border-destructive [&_input]:ring-destructive/20 [&_input]:ring-[3px]')}>
                                <AddressSearchInput
                                    value={data.address ?? ''}
                                    onChange={(value) => {
                                        clearErrors('address');
                                        setData('address', value);
                                        setData('address_street', null);
                                        setData('address_house_number', null);
                                        setData('address_postal_code', null);
                                        setData('address_city', null);
                                        setData('address_municipality', null);
                                        setData('address_country', null);
                                        setData('address_lat', null);
                                        setData('address_lng', null);
                                        setData('country', '');
                                        setHasAutoFilledFields(false);
                                    }}
                                    onAddressSelect={(address) => {
                                        clearErrors('address');
                                        setData('address', address.label);
                                        setData('address_street', address.street ?? null);
                                        setData('address_house_number', address.house_number ?? null);
                                        setData('address_postal_code', address.postal_code ?? null);
                                        setData('address_city', address.city ?? null);
                                        setData('address_municipality', address.city ?? null);
                                        setData('address_country', address.country_code ?? address.country ?? null);
                                        setData('address_lat', address.lat ?? null);
                                        setData('address_lng', address.lng ?? null);

                                        const resolvedCountry = resolveCountryFromAddress(address.country_code, address.country);
                                        if (resolvedCountry) {
                                            clearErrors('country');
                                            setData('country', resolvedCountry);
                                        }
                                    }}
                                    placeholder={t('companies.forms.add.placeholders.enter_address', 'Enter address')}
                                    className="w-full"
                                />
                            </div>
                        </GoogleMapsProvider>
                        <InputError message={errors.address} />
                    </div>

                    <div className="flex items-center gap-2 text-sm">
                        <span className="text-muted-foreground">{t('companies.forms.add.labels.country', 'Country')}:</span>
                        <span className="text-foreground font-medium">{countryLabel}</span>
                    </div>
                    <InputError message={errors.country} />

                    <div className="border-primary/25 bg-primary/5 flex items-start gap-2 rounded-sm border p-3 text-sm">
                        <Info className="text-primary mt-0.5 h-4 w-4 shrink-0" />
                        <p className="text-foreground">
                            {t('companies.forms.add.hints.country_auto_from_address', 'Country is auto-detected from selected address.')}
                        </p>
                    </div>
                </CardContent>
            </Card>

            <Card className="rounded-md">
                <CardHeader className="pb-4">
                    <div className="flex items-start gap-3">
                        <span className="bg-primary/12 text-primary inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold">
                            3
                        </span>
                        <div className="space-y-1">
                            <CardTitle className="flex items-center gap-2">
                                <Phone className="h-4 w-4" />
                                {t('companies.forms.add.sections_long.contact', 'Contact information')}
                            </CardTitle>
                            <CardDescription>
                                {t('companies.forms.add.sections_long.contact_desc', 'How customers and colleagues will reach this company.')}
                            </CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid items-start gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                            <Label htmlFor="phone" className="flex items-center gap-1.5">
                                <Phone className="text-muted-foreground h-3.5 w-3.5" />
                                {t('companies.forms.add.labels.phone', 'Phone')}
                            </Label>
                            <Input
                                id="phone"
                                value={data.phone}
                                aria-invalid={!!errors.phone}
                                className={cn(errors.phone && invalidFieldClassName)}
                                onChange={(e) => {
                                    clearErrors('phone');
                                    setData('phone', e.target.value);
                                }}
                                autoComplete="tel"
                                placeholder={t('companies.forms.add.placeholders.phone_number', 'Phone number')}
                            />
                            <InputError message={errors.phone} />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="email" className="flex items-center gap-1.5">
                                <Mail className="text-muted-foreground h-3.5 w-3.5" />
                                {t('companies.forms.add.labels.email', 'Email')}
                            </Label>
                            <Input
                                id="email"
                                type="email"
                                value={data.email}
                                aria-invalid={!!errors.email}
                                className={cn(errors.email && invalidFieldClassName)}
                                onChange={(e) => {
                                    clearErrors('email');
                                    setData('email', e.target.value);
                                }}
                                autoComplete="email"
                                placeholder={t('companies.forms.add.placeholders.company_email', 'Company email')}
                            />
                            <InputError message={errors.email} />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="domain">{t('companies.forms.add.labels.website', 'Website')}</Label>
                        <Input
                            id="domain"
                            value={data.domain}
                            aria-invalid={!!errors.domain}
                            className={cn(errors.domain && invalidFieldClassName)}
                            onChange={(e) => {
                                clearErrors('domain');
                                setData('domain', e.target.value);
                            }}
                            autoComplete="url"
                            placeholder={t('companies.forms.add.placeholders.domain', 'Domain')}
                        />
                        <InputError message={errors.domain} />
                    </div>
                </CardContent>
            </Card>

            <div className="bg-background/95 sticky bottom-0 z-10 flex justify-end gap-2 rounded-md border p-4 backdrop-blur">
                <Button type="button" variant="outline" onClick={onCancel ?? (() => window.history.back())}>
                    {t('companies.forms.add.actions.cancel', 'Cancel')}
                </Button>
                <Button type="submit" disabled={processing || submitting || isPrefilling}>
                    {processing || submitting
                        ? t('companies.forms.add.buttons.saving', 'Saving...')
                        : t('companies.forms.add.actions.create', 'Create company')}
                </Button>
            </div>
        </form>
    );
}
